import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarRangePicker,
  type CalendarAccent,
} from '../primitives/CalendarRangePicker'
import { Icon } from '../primitives/Icon'
import { nextFixedDurationRange, type FixedDurationPick } from '../../lib/fixed-duration'

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const DEFAULT_ACCENT: CalendarAccent = {
  swatch: 'var(--steel-blue)',
  bg: 'var(--primary-bg)',
  line: 'var(--steel-blue)',
  ink: 'var(--text)',
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export interface RangeCalendarProps {
  from: string
  to: string
  /** `pick` is set in fixed-duration mode only: which end the click set. */
  onChange: (from: string, to: string, pick?: FixedDurationPick) => void
  publicHolidays: ReadonlyArray<string>
  /** Optional name lookup for holidays — surfaces inside per-day tooltips. */
  holidayNames?: ReadonlyArray<{ date: string; name: string }>
  /** Optional ISO dates to mark as "teammate already off" (conflict). */
  conflictDates?: ReadonlyArray<string>
  /** Optional dated entries to mark as blackout windows. Dates are still
   *  selectable — they trigger escalation on submit, not rejection. */
  blackoutDates?: ReadonlyArray<{ date: string; name: string }>
  /** When true the blackout-window legend below the grid is suppressed —
   *  the caller surfaces that copy elsewhere (e.g. an info-icon tooltip). */
  hideBlackoutLegend?: boolean
  min?: string | undefined
  /** Latest selectable ISO date (inclusive). Dates after this are grayed out
   *  and not clickable. Passed through from the booking-window cap. */
  max?: string | undefined
  /** Single-date mode: picking one date fires onChange(date, date). */
  single?: boolean
  /** One-click single-day mode — the first click completes a 1-day range so
   *  day-part (AM/PM/Full) options can surface immediately. See
   *  CalendarRangePicker.oneClickSingleDay. Default OFF. */
  oneClickSingleDay?: boolean
  /** Fixed-duration mode: picking a start date pre-fills the end as
   *  startDate + fixedDuration - 1 (inclusive), stopped at `max`. A later
   *  click moves the end (never past the full block or `max`); an earlier
   *  click re-picks the start. See lib/fixed-duration. */
  fixedDuration?: number | undefined
  /** Mobile mode renders one month; desktop renders two side-by-side. */
  mobile?: boolean
  /** Color tokens for endpoints + range fill. Defaults to terracotta;
   *  ApplyLeavePage passes the LEAVE_TYPE_STYLE entry for the selected
   *  leave type so the calendar retints live. */
  accent?: CalendarAccent
  /** Overflow split: first ISO date that draws from Unpaid Leave instead
   *  of the original-type balance. Forwarded to CalendarRangePicker for
   *  bar-tint and to the overflow legend below the grid. */
  unpaidFromDate?: string | null
  /** Human-readable label of the original leave type, e.g. "Annual",
   *  "Medical". Renders in the overflow legend. Defaults to "Annual". */
  originalTypeLabel?: string
  /** Cap the range to this many days from the picked start (balance-capped
   *  leave types). Forwarded to both month grids. */
  maxSpanDays?: number
  /** When true, ``maxSpanDays`` counts elapsed (calendar) days. */
  spanCountsElapsedDays?: boolean
  /** Month to open on when nothing is selected (ISO date). Defaults to today. */
  initialView?: string | undefined
}

/**
 * Range calendar wrapper used in ApplyLeavePage. Owns shared from/to,
 * view-month state, and the prev/next nav row, then renders one (mobile)
 * or two (desktop) `CalendarRangePicker` grids side-by-side. Below the
 * grid: a blackout legend and an overflow legend when applicable.
 */
export function RangeCalendar({
  from,
  to,
  onChange,
  publicHolidays,
  holidayNames,
  conflictDates = [],
  blackoutDates = [],
  hideBlackoutLegend = false,
  min,
  max,
  single = false,
  oneClickSingleDay = false,
  fixedDuration,
  mobile = false,
  accent = DEFAULT_ACCENT,
  unpaidFromDate = null,
  originalTypeLabel = 'Annual',
  maxSpanDays,
  spanCountsElapsedDays = false,
  initialView,
}: RangeCalendarProps): JSX.Element {
  const today = todayIso()
  const anchor = from || initialView || today
  const [viewYear, setViewYear] = useState(() => Number(anchor.slice(0, 4)))
  const [viewMonth, setViewMonth] = useState(() => Number(anchor.slice(5, 7)) - 1)
  const [hover, setHover] = useState<string | null>(null)

  // Snap the calendar to the start date's month ONLY when that date is
  // outside the currently-rendered window (mobile = 1 month, desktop =
  // 2 months). This covers the cases that need a snap: saved-draft
  // restore on /apply, smart-prefill chips, EA acting-for hand-off —
  // any flow that hands in a `from` the user can't currently see. When
  // the user picks a date that's already visible (e.g. they click into
  // the second month of a desktop view), we leave the view parked so
  // the months don't lurch sideways under them.
  useEffect(() => {
    if (!from) return
    const targetYear = Number(from.slice(0, 4))
    const targetMonth = Number(from.slice(5, 7)) - 1
    if (Number.isNaN(targetYear) || Number.isNaN(targetMonth)) return
    const inFirstMonth = viewYear === targetYear && viewMonth === targetMonth
    const secondMonthY = viewMonth === 11 ? viewYear + 1 : viewYear
    const secondMonthM = viewMonth === 11 ? 0 : viewMonth + 1
    const inSecondMonth =
      !mobile && secondMonthY === targetYear && secondMonthM === targetMonth
    if (inFirstMonth || inSecondMonth) return
    setViewYear(targetYear)
    setViewMonth(targetMonth)
    // We want this to react only to external `from` changes — the user
    // clicking chevrons updates view state too, but in that case `from`
    // is unchanged so this effect won't fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from])

  const isFixedMode = fixedDuration !== undefined
  // Fixed-duration blocks charge calendar days, so the block itself is the
  // span cap: dates past start + duration - 1 grey out once a start is set.
  const spanCap = isFixedMode ? fixedDuration : maxSpanDays
  const spanElapsed = isFixedMode || spanCountsElapsedDays

  // The picker reports a click as (iso, '') when a range is already set and
  // as (from, iso) while picking the end, so the clicked date is `toVal` when
  // present, else `fromVal`.
  function handleFixedDurationChange(fromVal: string, toVal: string): void {
    if (!fixedDuration || !fromVal) {
      onChange(fromVal, toVal)
      return
    }
    const clicked = toVal !== '' ? toVal : fromVal
    const next = nextFixedDurationRange({ clicked, from, durationDays: fixedDuration, maxDate: max })
    onChange(next.from, next.to, next.pick)
  }

  // Holidays merged into a single { date, name } list so CRP shows tooltips.
  const mergedHolidays = useMemo(() => {
    const named = new Map((holidayNames ?? []).map((h) => [h.date, h.name]))
    return publicHolidays.map((date) => ({
      date,
      name: named.get(date) ?? '',
    }))
  }, [publicHolidays, holidayNames])

  const handleViewMonthChange = useCallback((y: number, m: number): void => {
    setViewYear(y)
    setViewMonth(m)
  }, [])

  // Slide animation when the visible month(s) shift. Without this, picking
  // a date in the second month snaps the view forward by one (e.g. May–Jun
  // → Jun–Jul) and users lose the connection between "I clicked here" and
  // "the calendar now shows different months". The grid is translated by a
  // small offset (right for forward, left for backward) then eased back to
  // 0, so the new content visibly enters from the same direction it would
  // have come from on a continuous timeline. Uses the Web Animations API
  // on a ref so the cells aren't remounted — props change synchronously
  // and the animation just plays on the next frame.
  const gridRef = useRef<HTMLDivElement | null>(null)
  const prevViewRef = useRef({ year: viewYear, month: viewMonth })
  useEffect(() => {
    const prev = prevViewRef.current
    if (prev.year === viewYear && prev.month === viewMonth) return
    const isForward =
      viewYear > prev.year ||
      (viewYear === prev.year && viewMonth > prev.month)
    prevViewRef.current = { year: viewYear, month: viewMonth }

    const el = gridRef.current
    if (!el || typeof el.animate !== 'function') return
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }

    const offset = isForward ? 28 : -28
    el.animate(
      [
        { transform: `translateX(${offset}px)`, opacity: 0.55 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'both' },
    )
  }, [viewYear, viewMonth])

  function prevMonth(): void {
    if (viewMonth === 0) handleViewMonthChange(viewYear - 1, 11)
    else handleViewMonthChange(viewYear, viewMonth - 1)
  }
  function nextMonth(): void {
    if (viewMonth === 11) handleViewMonthChange(viewYear + 1, 0)
    else handleViewMonthChange(viewYear, viewMonth + 1)
  }

  // Months to render: desktop = current + next, mobile = current only.
  const secondMonth = viewMonth === 11
    ? { year: viewYear + 1, month: 0 }
    : { year: viewYear, month: viewMonth + 1 }

  return (
    <div data-testid="range-calendar">
      {/* Outer nav row spanning both months */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <button
          type="button"
          onClick={prevMonth}
          aria-label="Previous month"
          style={NAV_BTN}
        >
          <Icon name="chevron_left" size={18} />
        </button>
        <span
          data-testid="range-calendar-month-label"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text)',
          }}
        >
          {MONTHS_SHORT[viewMonth]} {viewYear}
          {mobile ? '' : ` — ${MONTHS_SHORT[secondMonth.month]} ${secondMonth.year}`}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          aria-label="Next month"
          style={NAV_BTN}
        >
          <Icon name="chevron_right" size={18} />
        </button>
      </div>

      <div style={{ overflow: 'hidden' }}>
        <div
          ref={gridRef}
          style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
            gap: mobile ? 12 : 18,
            willChange: 'transform',
          }}
        >
          <CalendarRangePicker
            from={from}
            to={to}
            onChange={isFixedMode ? handleFixedDurationChange : onChange}
            min={min}
            max={max}
            single={single}
            oneClickSingleDay={oneClickSingleDay}
            publicHolidays={mergedHolidays}
            conflictDates={conflictDates}
            blackoutDates={blackoutDates}
            accent={accent}
            viewYear={viewYear}
            viewMonth={viewMonth}
            onViewMonthChange={handleViewMonthChange}
            hoverDate={isFixedMode ? null : hover}
            onHoverChange={isFixedMode ? undefined : setHover}
            hideNav
            fillWidth
            unpaidFromDate={unpaidFromDate}
            {...(spanCap !== undefined ? { maxSpanDays: spanCap } : {})}
            spanCountsElapsedDays={spanElapsed}
          />
          {mobile ? null : (
            <CalendarRangePicker
              from={from}
              to={to}
              onChange={isFixedMode ? handleFixedDurationChange : onChange}
              min={min}
              max={max}
              single={single}
              oneClickSingleDay={oneClickSingleDay}
              publicHolidays={mergedHolidays}
              conflictDates={conflictDates}
              blackoutDates={blackoutDates}
              accent={accent}
              viewYear={secondMonth.year}
              viewMonth={secondMonth.month}
              onViewMonthChange={handleViewMonthChange}
              hoverDate={isFixedMode ? null : hover}
              onHoverChange={isFixedMode ? undefined : setHover}
              hideNav
              unpaidFromDate={unpaidFromDate}
              {...(spanCap !== undefined ? { maxSpanDays: spanCap } : {})}
              spanCountsElapsedDays={spanElapsed}
            />
          )}
        </div>
      </div>

      {!single && !isFixedMode && from !== '' && to === '' ? (
        <div
          style={{
            marginTop: 8,
            textAlign: 'center',
            fontSize: 11,
            color: 'var(--text-3)',
            fontStyle: 'italic',
          }}
        >
          Pick an end date
        </div>
      ) : null}

      {unpaidFromDate && from !== '' && to !== '' && to >= unpaidFromDate ? (
        <div
          data-testid="range-calendar-overflow-legend"
          style={{
            marginTop: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            fontSize: 11,
            color: 'var(--text-3)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: accent.bg,
                border: `1px solid ${accent.swatch}`,
                flexShrink: 0,
              }}
            />
            {originalTypeLabel} leave
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background:
                  'repeating-linear-gradient(-45deg, ' +
                  'rgba(120, 120, 120, 0.32) 0px, ' +
                  'rgba(120, 120, 120, 0.32) 3px, ' +
                  'rgba(120, 120, 120, 0.08) 3px, ' +
                  'rgba(120, 120, 120, 0.08) 6px)',
                border: '1px solid var(--border)',
                flexShrink: 0,
              }}
            />
            Unpaid leave — Director must approve
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: 'rgba(120, 120, 120, 0.08)',
                border: '1px solid var(--border)',
                flexShrink: 0,
              }}
            />
            Public holiday or weekend — doesn’t count
          </span>
        </div>
      ) : null}

      {!hideBlackoutLegend && blackoutDates.length > 0 ? (
        <div
          data-testid="range-calendar-blackout-legend"
          style={{
            marginTop: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--text-3)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              flexShrink: 0,
              background: `repeating-linear-gradient(
                -45deg,
                var(--danger-bg) 0px,
                var(--danger-bg) 3px,
                var(--surface) 3px,
                var(--surface) 6px
              )`,
              border: '1px solid var(--danger-line)',
            }}
          />
          Blackout window — leave is still submittable but will be escalated
        </div>
      ) : null}

    </div>
  )
}

const NAV_BTN = {
  width: 28,
  height: 28,
  padding: 0,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text-2)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const
